import { Box, FormControl, FormControlLabel, RadioGroup, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import InputCheckbox from 'component/Input/InputCheckbox'
import InputRadio from 'component/Input/InputRadio'
import { colors } from 'constants/theme'

export interface PatientSearchSetting {
  mode: 'default' | 'custom'
  patientIdType: 'cnNumber' | 'branchCnNumber'
  fields: {
    displayName: boolean
    idCard: boolean
    phone: boolean
    email: boolean
    nameEn: boolean
    snCode: boolean
  }
}

export const DEFAULT_PATIENT_SEARCH_SETTING: PatientSearchSetting = {
  mode: 'default',
  patientIdType: 'branchCnNumber',
  fields: {
    displayName: true,
    idCard: true,
    phone: true,
    email: false,
    nameEn: true,
    snCode: false
  }
}

interface Props {
  valArr?: any
  onchange?: (value: any) => void
}

export default function FormSearchSetting(props: Props) {
  const { t } = useTranslation()

  const current: PatientSearchSetting = useMemo(() => {
    const raw = props.valArr?.patientSearchSetting
    if (!raw) return DEFAULT_PATIENT_SEARCH_SETTING
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return {
      mode: parsed?.mode || DEFAULT_PATIENT_SEARCH_SETTING.mode,
      patientIdType: parsed?.patientIdType || DEFAULT_PATIENT_SEARCH_SETTING.patientIdType,
      fields: { ...DEFAULT_PATIENT_SEARCH_SETTING.fields, ...(parsed?.fields || {}) }
    }
  }, [props.valArr?.patientSearchSetting])

  useEffect(() => {
    if (!props.valArr?.patientSearchSetting && props.onchange) {
      props.onchange({ ...(props.valArr || {}), patientSearchSetting: DEFAULT_PATIENT_SEARCH_SETTING })
    }
  }, [])

  const update = (next: PatientSearchSetting) => {
    if (props.onchange) props.onchange({ ...(props.valArr || {}), patientSearchSetting: next })
  }

  const setMode = (mode: 'default' | 'custom') => update({ ...current, mode })
  const setPatientIdType = (patientIdType: 'cnNumber' | 'branchCnNumber') => update({ ...current, patientIdType })
  const toggleField = (key: keyof PatientSearchSetting['fields'], value: boolean) =>
    update({ ...current, fields: { ...current.fields, [key]: value } })

  const customDisabled = current.mode !== 'custom'

  return (
    <Box className="px-4 py-4">
      <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.themeMainColor }}>
        {t('CLINIC_MANAGEMENT.SEARCH_SETTING.TITLE')}
      </Typography>

      <Box className="pt-4">
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('CLINIC_MANAGEMENT.SEARCH_SETTING.MODE')}</Typography>
        <FormControl>
          <RadioGroup value={current.mode} onChange={(e) => setMode(e.target.value as any)}>
            <FormControlLabel value="default" control={<InputRadio />} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.MODE_DEFAULT')} />
            <FormControlLabel value="custom" control={<InputRadio />} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.MODE_CUSTOM')} />
          </RadioGroup>
        </FormControl>
      </Box>

      <Box className="pt-3">
        <Typography sx={{ fontWeight: 600, mb: 1, color: customDisabled ? colors.disabledGray : colors.themeMainColor }}>
          {t('CLINIC_MANAGEMENT.SEARCH_SETTING.PATIENT_ID_TYPE')}
        </Typography>
        <FormControl disabled={customDisabled} sx={{ '& .Mui-disabled.Mui-checked': { color: `${colors.disabledGray} !important` } }}>
          <RadioGroup value={current.patientIdType} onChange={(e) => setPatientIdType(e.target.value as any)}>
            <FormControlLabel value="cnNumber" control={<InputRadio />} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.CN_NUMBER')} disabled={customDisabled} />
            <FormControlLabel value="branchCnNumber" control={<InputRadio />} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.BRANCH_CN_NUMBER')} disabled={customDisabled} />
          </RadioGroup>
        </FormControl>
      </Box>

      <Box className="pt-3">
        <Typography sx={{ fontWeight: 600, mb: 1, color: customDisabled ? colors.disabledGray : colors.themeMainColor }}>
          {t('CLINIC_MANAGEMENT.SEARCH_SETTING.ALWAYS_ON')}
        </Typography>
        <InputCheckbox checked disabled label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.NAME_TH')} />
      </Box>

      <Box className="pt-3">
        <Typography sx={{ fontWeight: 600, mb: 1, color: customDisabled ? colors.disabledGray : colors.themeMainColor }}>
          {t('CLINIC_MANAGEMENT.SEARCH_SETTING.OPTIONAL_FIELDS')}
        </Typography>
        <Box className="d-flex flex-column">
          <InputCheckbox checked={current.fields.displayName} disabled={customDisabled} onChange={(e) => toggleField('displayName', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.DISPLAY_NAME')} />
          <InputCheckbox checked={current.fields.idCard} disabled={customDisabled} onChange={(e) => toggleField('idCard', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.ID_CARD')} />
          <InputCheckbox checked={current.fields.phone} disabled={customDisabled} onChange={(e) => toggleField('phone', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.PHONE')} />
          <InputCheckbox checked={current.fields.email} disabled={customDisabled} onChange={(e) => toggleField('email', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.EMAIL')} />
          <InputCheckbox checked={current.fields.nameEn} disabled={customDisabled} onChange={(e) => toggleField('nameEn', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.NAME_EN')} />
          <InputCheckbox checked={current.fields.snCode} disabled={customDisabled} onChange={(e) => toggleField('snCode', e.target.checked)} label={t('CLINIC_MANAGEMENT.SEARCH_SETTING.SN_CODE')} />
        </Box>
      </Box>
    </Box>
  )
}
